'use client'

import React from 'react'
import {
  Layers,
  FileText,
  Brain,
  MessageSquare,
  ArrowDown,
  AlertTriangle,
  RefreshCw,
  Database,
  Zap,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  GitBranch,
} from 'lucide-react'
import { Github } from 'lucide-react'
import BlogCTACard from './BlogCTACard'

/**
 * Context Engineering Diagrams
 * Visual components for the context engineering blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Context Hierarchy Diagram — 4 context layers with token budget
 */
export function ContextHierarchyDiagram() {
  const layers = [
    {
      name: 'System Prompt',
      description: 'Model identity, safety rules, base capabilities',
      budget: '~5%',
      persistence: 'Permanent',
      icon: Layers,
      color: '#F90B8A',
      width: '45%',
    },
    {
      name: 'CLAUDE.md / Project Config',
      description: 'Codebase standards, architecture, file paths',
      budget: '~15%',
      persistence: 'Per project',
      icon: FileText,
      color: '#8b5cf6',
      width: '60%',
    },
    {
      name: 'Session Memory',
      description: 'Auto-memory, compaction summaries, task state',
      budget: '~20%',
      persistence: 'Per session',
      icon: Database,
      color: '#FDC304',
      width: '75%',
    },
    {
      name: 'Conversation / Working Context',
      description: 'Current files, tool results, user messages',
      budget: '~60%',
      persistence: 'Ephemeral',
      icon: MessageSquare,
      color: '#008dcc',
      width: '90%',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE CONTEXT HIERARCHY
        </h3>
        <p className='text-sm text-muted-foreground'>
          Four layers of context with different persistence. Token shares are
          illustrative, not measured
        </p>
      </div>

      <div className='flex flex-col items-center gap-3'>
        {layers.map((layer, index) => (
          <React.Fragment key={layer.name}>
            <div
              className='rounded-xl bg-background border border-border/60 p-4'
              style={{
                width: layer.width,
                borderLeftWidth: '4px',
                borderLeftColor: layer.color,
              }}
            >
              <div className='flex items-start gap-3'>
                <div
                  className='p-2 rounded-lg flex-shrink-0'
                  style={{ backgroundColor: `${layer.color}20` }}
                >
                  <layer.icon
                    className='w-4 h-4'
                    style={{ color: layer.color }}
                  />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center justify-between gap-2'>
                    <span className='text-sm font-semibold text-foreground'>
                      {layer.name}
                    </span>
                    <span
                      className='text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0'
                      style={{
                        backgroundColor: `${layer.color}20`,
                        color: layer.color,
                      }}
                    >
                      {layer.budget}
                    </span>
                  </div>
                  <p className='text-[10px] text-muted-foreground mt-0.5'>
                    {layer.description}
                  </p>
                  <p
                    className='text-[10px] font-medium mt-0.5'
                    style={{ color: layer.color }}
                  >
                    {layer.persistence}
                  </p>
                </div>
              </div>
            </div>
            {index < layers.length - 1 && (
              <ArrowDown className='w-4 h-4 text-muted-foreground' />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className='mt-6 px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#008dcc]'>Key insight:</span>{' '}
          Higher layers are more persistent but consume fewer tokens. Optimize
          from the top down: small changes to CLAUDE.md can yield larger
          improvements than refining individual prompts.
        </p>
      </div>
    </div>
  )
}

/**
 * Context Window Optimization Diagram — Before/After
 */
export function ContextWindowOptimizationDiagram() {
  const before = [
    { label: 'Duplicate file reads', pct: 25, color: '#ef4444' },
    { label: 'Stale conversation', pct: 35, color: '#f97316' },
    { label: 'Useful context', pct: 30, color: '#10b981' },
    { label: 'Wasted on noise', pct: 10, color: '#ef4444' },
  ]

  const after = [
    { label: 'Compacted summaries', pct: 10, color: '#008dcc' },
    { label: 'Targeted file reads', pct: 15, color: '#8b5cf6' },
    { label: 'Useful context', pct: 60, color: '#10b981' },
    { label: 'Reserve capacity', pct: 15, color: '#FDC304' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          CONTEXT WINDOW: BEFORE vs AFTER
        </h3>
        <p className='text-sm text-muted-foreground'>
          Optimized context engineering doubles usable working memory
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Before */}
        <div>
          <div className='flex items-center gap-2 mb-3'>
            <XCircle className='w-4 h-4 text-red-500' />
            <span className='text-sm font-bold text-red-500'>Unoptimized</span>
          </div>
          <div className='w-full h-8 rounded-lg overflow-hidden flex'>
            {before.map(seg => (
              <div
                key={seg.label}
                className='h-full'
                style={{
                  width: `${seg.pct}%`,
                  backgroundColor: seg.color,
                }}
                title={`${seg.label}: ${seg.pct}%`}
              />
            ))}
          </div>
          <div className='mt-2 space-y-1'>
            {before.map(seg => (
              <div
                key={seg.label}
                className='flex items-center gap-2 text-[10px]'
              >
                <div
                  className='w-2 h-2 rounded-full flex-shrink-0'
                  style={{ backgroundColor: seg.color }}
                />
                <span className='text-muted-foreground'>{seg.label}</span>
                <span className='font-bold text-foreground ml-auto'>
                  {seg.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* After */}
        <div>
          <div className='flex items-center gap-2 mb-3'>
            <CheckCircle className='w-4 h-4 text-green-600 dark:text-green-400' />
            <span className='text-sm font-bold text-green-600 dark:text-green-400'>
              Optimized
            </span>
          </div>
          <div className='w-full h-8 rounded-lg overflow-hidden flex'>
            {after.map(seg => (
              <div
                key={seg.label}
                className='h-full'
                style={{
                  width: `${seg.pct}%`,
                  backgroundColor: seg.color,
                }}
                title={`${seg.label}: ${seg.pct}%`}
              />
            ))}
          </div>
          <div className='mt-2 space-y-1'>
            {after.map(seg => (
              <div
                key={seg.label}
                className='flex items-center gap-2 text-[10px]'
              >
                <div
                  className='w-2 h-2 rounded-full flex-shrink-0'
                  style={{ backgroundColor: seg.color }}
                />
                <span className='text-muted-foreground'>{seg.label}</span>
                <span className='font-bold text-foreground ml-auto'>
                  {seg.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key stats */}
      <div className='grid grid-cols-3 gap-3 mt-6'>
        <div className='p-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30 text-center'>
          <p className='text-2xl font-bold text-green-600 dark:text-green-400'>
            2x
          </p>
          <p className='text-[10px] text-muted-foreground'>Usable context</p>
        </div>
        <div className='p-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
          <p className='text-2xl font-bold text-[#008dcc]'>+10%</p>
          <p className='text-[10px] text-muted-foreground'>Code quality gain</p>
        </div>
        <div className='p-3 rounded-xl bg-[#FDC304]/10 border border-[#FDC304]/30 text-center'>
          <p className='text-2xl font-bold text-[#FDC304]'>3x</p>
          <p className='text-[10px] text-muted-foreground'>Longer sessions</p>
        </div>
      </div>
    </div>
  )
}

/**
 * Context Rot Diagram — Degradation and recovery flow
 */
export function ContextRotDiagram() {
  const stages = [
    {
      stage: 'Fresh Context',
      description: 'Session starts with full project awareness',
      quality: 100,
      icon: Zap,
      color: '#10b981',
    },
    {
      stage: 'Context Drift',
      description: 'Conversation fills window, old info pushed out',
      quality: 70,
      icon: Clock,
      color: '#FDC304',
    },
    {
      stage: 'Context Rot',
      description: 'Agent loses track of earlier decisions and files',
      quality: 40,
      icon: AlertTriangle,
      color: '#f97316',
    },
    {
      stage: 'Hallucination Risk',
      description: 'Agent fabricates details to fill knowledge gaps',
      quality: 15,
      icon: XCircle,
      color: '#ef4444',
    },
  ]

  const solutions = [
    {
      name: 'Conversation Compaction',
      description: 'Auto-summarize older messages',
      icon: RefreshCw,
      color: '#008dcc',
    },
    {
      name: 'Persistent Memory',
      description: 'Store decisions across sessions',
      icon: Database,
      color: '#8b5cf6',
    },
    {
      name: 'Subagent Delegation',
      description: 'Offload research to fresh contexts',
      icon: Brain,
      color: '#F90B8A',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          CONTEXT ROT: THE SILENT KILLER
        </h3>
        <p className='text-sm text-muted-foreground'>
          How context quality degrades in long sessions, and three patterns that
          solve it
        </p>
      </div>

      {/* Degradation visualization */}
      <div className='mb-6'>
        <div className='flex items-end justify-between gap-2 h-32'>
          {stages.map(s => (
            <div
              key={s.stage}
              className='flex-1 flex flex-col items-center gap-1'
            >
              <s.icon
                className='w-4 h-4 flex-shrink-0'
                style={{ color: s.color }}
              />
              <div
                className='w-full rounded-t-lg transition-all'
                style={{
                  height: `${s.quality}%`,
                  backgroundColor: `${s.color}30`,
                  borderTop: `3px solid ${s.color}`,
                }}
              />
            </div>
          ))}
        </div>
        <div className='flex justify-between gap-2 mt-2'>
          {stages.map(s => (
            <div key={s.stage} className='flex-1 text-center'>
              <p className='text-[10px] font-bold' style={{ color: s.color }}>
                {s.stage}
              </p>
              <p className='text-[9px] text-muted-foreground hidden md:block'>
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow */}
      <div className='flex justify-center my-4'>
        <div className='flex items-center gap-2 px-4 py-2 rounded-full bg-[#10b981]/10 border border-[#10b981]/30'>
          <TrendingUp className='w-4 h-4 text-green-600 dark:text-green-400' />
          <span className='text-xs font-semibold text-green-600 dark:text-green-400'>
            Recovery Patterns
          </span>
        </div>
      </div>

      {/* Solutions */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
        {solutions.map(sol => (
          <div
            key={sol.name}
            className='p-3 rounded-xl bg-background border-l-4 border border-border/60'
            style={{ borderLeftColor: sol.color }}
          >
            <div className='flex items-center gap-2 mb-1'>
              <sol.icon className='w-4 h-4' style={{ color: sol.color }} />
              <span className='text-xs font-semibold text-foreground'>
                {sol.name}
              </span>
            </div>
            <p className='text-[10px] text-muted-foreground'>
              {sol.description}
            </p>
          </div>
        ))}
      </div>

      <div className='mt-4 px-4 py-3 rounded-xl bg-[#F90B8A]/10 border border-[#F90B8A]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#F90B8A]'>Prevention:</span> Keep
          sessions focused. When context drifts beyond the current task, start a
          new thread with a fresh context window.
        </p>
      </div>
    </div>
  )
}

/**
 * Thread Engineering Kit CTA Card
 */
export function ThreadKitCTA() {
  return (
    <BlogCTACard
      href='https://github.com/lloydpilapil/thread-engineering-kit'
      label='Free Download'
      title='Thread Engineering Kit'
      description='9 open-source execution patterns for Claude Code. Parallel work, chained phases, autonomous execution, competitive evaluation, and more.'
      buttonText='View on GitHub'
      ButtonIcon={Github}
      SideIcon={GitBranch}
    />
  )
}
